import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { colorForSlug } from '@/utils/tasks_catalog';
import { hexToRgba } from '@/utils/agent_catalog';

const MENU_MAX_HEIGHT = 440;
const CHIP_ACTIVE_BG_ALPHA = 0.12;

/** Subtle chip fill for an accent color (hex or rgba). Never append hex alpha to rgba strings. */
function accentChipBackground(color, alpha = CHIP_ACTIVE_BG_ALPHA) {
  if (color == null || color === '') return undefined;
  const s = String(color).trim();
  if (s.startsWith('#')) {
    return hexToRgba(s, alpha);
  }
  const rgbaMatch = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbaMatch) {
    return `rgba(${rgbaMatch[1]},${rgbaMatch[2]},${rgbaMatch[3]},${alpha})`;
  }
  if (/^[0-9a-f]{3,8}$/i.test(s)) {
    return hexToRgba(s, alpha);
  }
  return undefined;
}
const SCROLL_FOOTER_HEIGHT = 30;
const ALL_CATEGORIES_ID = '__all__';

/**
 * Scrollable category filter for catalog pages (tools, tasks, models, decks, explore).
 *
 * @param {Object[]} categories — `{ slug, name, icon?, count? }` or pass `getCategoryCount`
 * @param {Object[]} [topItems] — pinned rows before categories (`{ key, label, icon, isActive, onSelect, tint?, iconSolid? }`)
 * @param {boolean} [showAllOption=true] — "All categories" row when no active top item covers "all"
 */
function CatalogCategoryDropdown({
  categories = [],
  activeCategorySlug = null,
  onChangeCategory,
  topItems = [],
  showAllOption = true,
  allOptionLabel = 'All categories',
  defaultTriggerLabel = 'Categories',
  scrollForMoreLabel = 'Scroll for more',
  getCategoryCount,
  formatCountLabel,
  getCategoryTint = colorForSlug,
  getCategoryIcon = (cat) => cat?.icon || 'tag',
  variant = 'default',
  style,
  menuScrollNativeId = 'catalog-category-menu-scroll',
  menuContentClassName = 'CatalogCategoryDropdown',
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const isChip = variant === 'toolbarChip';
  const scrollRef = useRef(null);
  const [showScrollFooter, setShowScrollFooter] = useState(false);

  const resolveCount = useCallback((cat) => {
    if (typeof getCategoryCount === 'function') return getCategoryCount(cat);
    return cat?.count ?? cat?.stats?.itemCount ?? cat?.stats?.toolCount ?? null;
  }, [getCategoryCount]);

  const formatCount = useCallback((count) => {
    if (typeof formatCountLabel === 'function') return formatCountLabel(count);
    if (typeof count !== 'number' || count <= 0) return null;
    return count === 1 ? '1 item' : `${count} items`;
  }, [formatCountLabel]);

  const updateScrollFooter = useCallback((offsetY, viewportH, contentH) => {
    if (!contentH || !viewportH) {
      setShowScrollFooter(false);
      return;
    }
    setShowScrollFooter(offsetY + viewportH < contentH - 8);
  }, []);

  const measureScrollFooter = useCallback((offsetY = 0) => {
    const node = scrollRef.current;
    if (!node) return;
    const viewportH = node.clientHeight ?? MENU_MAX_HEIGHT;
    const contentH = node.scrollHeight ?? 0;
    updateScrollFooter(offsetY, viewportH, contentH);
  }, [updateScrollFooter]);

  const handleScroll = useCallback((e) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    updateScrollFooter(contentOffset.y, layoutMeasurement.height, contentSize.height);
  }, [updateScrollFooter]);

  const handleContentSizeChange = useCallback(() => {
    requestAnimationFrame(() => measureScrollFooter(0));
  }, [measureScrollFooter]);

  const handleScrollLayout = useCallback(() => {
    requestAnimationFrame(() => measureScrollFooter(0));
  }, [measureScrollFooter]);

  const activePreset = useMemo(
    () => topItems.find((item) => item.isActive) || null,
    [topItems],
  );

  const activeCategory = useMemo(
    () => (activeCategorySlug ? categories.find((c) => c.slug === activeCategorySlug) : null),
    [categories, activeCategorySlug],
  );

  const activeTint = useMemo(() => {
    if (activePreset?.tint) return activePreset.tint;
    if (activeCategorySlug) return getCategoryTint(activeCategorySlug);
    return colors.text_color_primary;
  }, [activePreset, activeCategorySlug, getCategoryTint, colors.text_color_primary]);

  const triggerIcon = activePreset?.icon ?? getCategoryIcon(activeCategory) ?? 'tag';
  const triggerLabel = activePreset?.label
    ?? activeCategory?.name
    ?? activeCategorySlug
    ?? defaultTriggerLabel;

  const showAllRow = showAllOption && !activePreset;

  const renderMenuItems = () => {
    const rows = [];

    for (const item of topItems) {
      rows.push(
        <DropdownMenuItem
          key={item.key}
          icon={item.icon}
          title={item.label}
          isActive={item.isActive}
          onSelect={item.onSelect}
        />,
      );
    }

    if (showAllRow) {
      rows.push(
        <DropdownMenuItem
          key={ALL_CATEGORIES_ID}
          icon="grid-2"
          title={allOptionLabel}
          isActive={!activeCategorySlug && !activePreset}
          onSelect={() => onChangeCategory?.(null)}
        />,
      );
    }

    if (categories.length > 0 && (topItems.length > 0 || showAllRow)) {
      rows.push(<DropdownMenu.Separator key="sep" className="DropdownMenuSeparator" />);
    }

    for (const cat of categories) {
      const slug = cat.slug;
      const countLabel = formatCount(resolveCount(cat));
      const name = cat.name || slug;
      const title = countLabel ? `${name} · ${countLabel}` : name;
      rows.push(
        <DropdownMenuItem
          key={slug}
          icon={getCategoryIcon(cat)}
          title={title}
          isActive={!activePreset && activeCategorySlug === slug}
          onSelect={() => onChangeCategory?.(slug)}
        />,
      );
    }

    return rows;
  };

  const renderNativeMenuItems = () => (
    <>
      {topItems.map((item) => (
        <DropdownMenu.Item key={item.key} onSelect={item.onSelect}>
          <DropdownMenu.ItemIcon ios={{ name: 'square.grid.2x2' }} />
          <DropdownMenu.ItemTitle>{item.label}</DropdownMenu.ItemTitle>
        </DropdownMenu.Item>
      ))}
      {showAllRow ? (
        <DropdownMenu.Item key={ALL_CATEGORIES_ID} onSelect={() => onChangeCategory?.(null)}>
          <DropdownMenu.ItemIcon ios={{ name: 'square.grid.2x2' }} />
          <DropdownMenu.ItemTitle>{allOptionLabel}</DropdownMenu.ItemTitle>
        </DropdownMenu.Item>
      ) : null}
      {(topItems.length > 0 || showAllRow) && categories.length > 0 ? <DropdownMenu.Separator /> : null}
      {categories.map((cat) => {
        const countLabel = formatCount(resolveCount(cat));
        const name = cat.name || cat.slug;
        const title = countLabel ? `${name} · ${countLabel}` : name;
        return (
          <DropdownMenu.Item key={cat.slug} onSelect={() => onChangeCategory?.(cat.slug)}>
            <DropdownMenu.ItemIcon ios={{ name: 'tag' }} />
            <DropdownMenu.ItemTitle>{title}</DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        );
      })}
    </>
  );

  const menuClass = Platform.OS === 'web'
    ? `DropdownMenuContent ${menuContentClassName}`
    : undefined;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Pressable
          style={({ hovered }) => [
            styles.trigger,
            isChip && styles.triggerChip,
            !isChip && styles.triggerToolbar,
            !isChip && isXS && styles.triggerCompact,
            style,
            {
              backgroundColor: isChip
                ? ((activePreset || activeCategorySlug)
                  ? (accentChipBackground(activeTint) ?? colors.solid_color_primary)
                  : colors.solid_color_primary)
                : (hovered ? colors.solid_color_tertiary : colors.solid_color_block),
              borderColor: isChip && (activePreset || activeCategorySlug)
                ? activeTint
                : colors.border_color_secondary,
            },
          ]}
          accessibilityRole="button"
          accessibilityLabel={triggerLabel}
        >
          <Icon
            name={triggerIcon}
            size={isChip ? 13 : 12}
            color={isChip ? activeTint : colors.text_color_tertiary}
            solid={isChip && !!(activePreset?.iconSolid || activeCategorySlug)}
          />
          <Text
            size={isChip ? 'xs' : 'xs'}
            weight={isChip ? 'medium' : 'bold'}
            numberOfLines={1}
            style={[
              {
                color: colors.text_color_primary,
                maxWidth: isChip ? undefined : (isXS ? 96 : 140),
              },
              isChip && styles.triggerChipLabel,
            ]}
          >
            {triggerLabel}
          </Text>
          <Icon name="chevron-down" size={isChip ? 10 : 9} color={colors.text_color_tertiary} />
        </Pressable>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={menuClass}
        align={isChip ? 'center' : 'end'}
        sideOffset={4}
        style={{ minWidth: 240, maxWidth: 300, padding: 4 }}
      >
        {Platform.OS === 'web' ? (
          <View style={styles.menuPanel}>
            <ScrollView
              ref={scrollRef}
              style={[
                styles.menuScroll,
                showScrollFooter && { maxHeight: MENU_MAX_HEIGHT - SCROLL_FOOTER_HEIGHT },
              ]}
              contentContainerStyle={styles.menuScrollContent}
              showsVerticalScrollIndicator
              nestedScrollEnabled
              scrollEventThrottle={16}
              onScroll={handleScroll}
              onContentSizeChange={handleContentSizeChange}
              onLayout={handleScrollLayout}
              nativeID={menuScrollNativeId}
            >
              {renderMenuItems()}
            </ScrollView>
            {showScrollFooter ? (
              <View
                style={[
                  styles.scrollFooter,
                  {
                    borderTopColor: colors.border_color_secondary,
                    backgroundColor: colors.solid_color_block,
                  },
                ]}
                pointerEvents="none"
              >
                <Icon name="chevron-down" size={9} color={colors.text_color_tertiary} />
                <Text size="xxs" weight="semibold" color="tertiary">
                  {scrollForMoreLabel}
                </Text>
              </View>
            ) : null}
          </View>
        ) : (
          renderNativeMenuItems()
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 1,
    maxWidth: 200,
    flexShrink: 1,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  triggerToolbar: {
    height: 32,
    paddingVertical: 0,
    paddingHorizontal: 12,
  },
  triggerCompact: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    maxWidth: 132,
    gap: 5,
  },
  triggerChip: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    height: 36,
    justifyContent: 'center',
    paddingHorizontal: 10,
    borderRadius: 10,
    maxWidth: undefined,
  },
  triggerChipLabel: {
    flex: 1,
    minWidth: 0,
  },
  menuPanel: {
    maxHeight: MENU_MAX_HEIGHT,
    overflow: 'hidden',
  },
  menuScroll: {
    maxHeight: MENU_MAX_HEIGHT,
  },
  menuScrollContent: {
    flexGrow: 0,
    paddingVertical: 2,
  },
  scrollFooter: {
    height: SCROLL_FOOTER_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
});

export default React.memo(CatalogCategoryDropdown);
